import React from "react";

/**
 * InlineNotice
 * Small, consistent in-app notice block for info/success/warning/error.
 *
 * Usage:
 * <InlineNotice variant="info" title="Heads up">Copy here</InlineNotice>
 * <InlineNotice variant="error" title="Something went wrong" actionLabel="Retry" onAction={...}>
 *   Try again in a moment.
 * </InlineNotice>
 */

function ActionButton({ actionLabel, onAction, href }) {
  if (!actionLabel) return null;
  if (href) {
    return (
      <a
        href={href}
        className="text-sm font-semibold underline underline-offset-2 hover:opacity-90"
      >
        {actionLabel}
      </a>
    );
  }
  if (onAction) {
    return (
      <button
        type="button"
        onClick={onAction}
        className="text-sm font-semibold underline underline-offset-2 hover:opacity-90"
      >
        {actionLabel}
      </button>
    );
  }
  return null;
}

function getVariantStyles(variant) {
  switch (variant) {
    case "success":
      return {
        icon: "✅",
        bg: "bg-emerald-500/10",
        border: "border-emerald-500/25",
        text: "text-emerald-50",
      };
    case "warning":
      return {
        icon: "⚠️",
        bg: "bg-amber-500/10",
        border: "border-amber-500/25",
        text: "text-amber-50",
      };
    case "error":
      return {
        icon: "⛔",
        bg: "bg-rose-500/10",
        border: "border-rose-500/25",
        text: "text-rose-50",
      };
    case "info":
    default:
      return {
        icon: "ℹ️",
        bg: "bg-white/5",
        border: "border-white/10",
        text: "text-white",
      };
  }
}

export default function InlineNotice({
  variant = "info",
  title,
  children,
  actionLabel,
  onAction,
  href,
  className = "",
  compact = false,
  testId,
}) {
  const styles = getVariantStyles(variant);
  const padding = compact ? "px-3 py-2" : "px-4 py-3";

  return (
    <div
      data-testid={testId}
      className={[
        "w-full rounded-2xl border",
        padding,
        "flex items-start gap-3",
        styles.bg,
        styles.border,
        styles.text,
        className,
      ].join(" ")}
      role={variant === "error" ? "alert" : "status"}
    >
      <div className="mt-0.5 text-lg leading-none">{styles.icon}</div>
      <div className="flex-1 min-w-0">
        {title ? (
          <div className="font-semibold text-sm leading-snug">{title}</div>
        ) : null}
        {children ? (
          <div className={["text-sm leading-snug", title ? "mt-1" : ""].join(" ")}>
            {children}
          </div>
        ) : null}
        {actionLabel ? (
          <div className="mt-2">
            <ActionButton actionLabel={actionLabel} onAction={onAction} href={href} />
          </div>
        ) : null}
      </div>
    </div>
  );
}
